import {createContext, useContext} from 'react'

import {isNumeric} from '../utils.js'
import Quantity from './Quantity.js'

/**
 * plural is a formatting function to add 's'es to words
 *
 * Examples
 *
 * @example
 * // As a simple format function
 * plural(5, 'es', 'oos', 'farts')
 * // return 'fartses'
 * plural(1, 'es', 'oos', 'farts')
 * // return 'fartsoos'
 * plural(5, 'es')
 * // return 'es'
 * plural(0, 'es', 'oos', 'farts')
 * // return 'fartses'
 * plural(1, 'es')
 * // return ''
 * plural(1, 'es', 'oos')
 * // return 'oos'
 *
 * @example
 * // As a template function
 * plural(5)`this is fun${['aans', 'ooos']} or what${['s']}`
 * // return 'this is funaans or whats'
 * plural(1)`this is fun${['aans', 'ooos']} or what${['s']}`
 * // return 'this is funooos or what'
 * plural(0)`this is fun${['aans', 'ooos']} or what${['s']}`
 * // return 'this is funaans or whats'
 *
 * @param {Array|Number} [thing] what we check for the count
 * @param {String} [plural] string to use if plural (concats with "word")
 * @param {String} [single] string to use if single (concats with "word")
 * @param {String} [word] A word to prefix "plural" or "single" to
 * @returns {String}
 */
export function plural(thing, plural, single, word) {
  const count = Array.isArray(thing)
    ? thing.length
    : isNumeric(thing)
      ? Number(thing)
      : 1

  if (plural !== undefined) {
    word = word || ''
    single = single || ''

    return `${word}${count === 1 ? single : plural}`
  }

  return (stringParts, ...params) => {
    return stringParts
      .reduce((prev, part, index) => {
        prev.push(part)

        if (index > params.length - 1) {
          return prev
        }

        const value = params[index]

        if (value === Count) {
          prev.push(count)
        } else if (Array.isArray(value)) {
          prev.push((count === 1 ? value[1] : value[0]) || '')
        } else {
          prev.push(value)
        }

        return prev
      }, [])
      .join('')
  }
}

// A context for sharing a common count in a PluralBlock
const PluralContext = createContext({})

/**
 * A way to form a statement that may contain multiple pluralization based on the same count
 *
 * @example
 * <PluralBlock count={some_number_of_things}>
 *   You are about to create <Count /> <Plural word="order" />. Are
 *   you sure you want to create <Plural p="these orders" s="this order" />?
 * </PluralBlock>
 *
 * @param {Object} props
 * @param {Number} [props.count] Number of things to count
 * @param {Array} [props.array] The array to base count on
 * @param {React.ReactNode} props.children
 * @returns {React.ReactNode}
 */
export function PluralBlock({count, array, children}) {
  count = count !== undefined ? count : array !== undefined ? array.length : 1

  return (
    <PluralContext.Provider value={{count}}>{children}</PluralContext.Provider>
  )
}

/**
 * Used to denote a point of pluralization. It can be used independent of PluralBlock or inside it
 *
 * @param {Object} props
 * @param {String} [props.word] word to pluralize
 * @param {String} [props.s] single version of word
 * @param {String} [props.p] plural version of word (default 's')
 * @param {Number} [props.count] Number of things to count
 * @param {Array} [props.array] The array to base count on
 * @param {React.ReactNode} [props.children]
 * @returns {React.ReactNode}
 */
export function Plural({word = '', s = '', p = 's', count, array, children}) {
  const context = useContext(PluralContext)
  count =
    count !== undefined
      ? count
      : array !== undefined
        ? array.length
        : context.count !== undefined
          ? context.count
          : 1
  const single = s
  const plural = p

  const str = `${word}${count === 1 ? single : plural}`

  if (children) {
    return (
      <>
        {children}
        {str}
      </>
    )
  }

  return str
}

/**
 * Show the count
 *
 * @param {Object} props
 * @param {Object} props.options Quantity options
 * @returns {React.ReactNode}
 */
export function Count({options}) {
  const context = useContext(PluralContext)
  const count = context.count !== undefined ? context.count : 1

  return <Quantity value={count} options={options} />
}

/**
 * Show if single
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @returns {React.ReactNode}
 */
export function IfSingle({children}) {
  const context = useContext(PluralContext)
  const count = context.count !== undefined ? context.count : 1

  return count === 1 ? children : null
}

/**
 * Show if plural
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @returns {React.ReactNode}
 */
export function IfPlural({children}) {
  const context = useContext(PluralContext)
  const count = context.count !== undefined ? context.count : 1

  return count !== 1 ? children : null
}

/**
 * Show if zero
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @returns {React.ReactNode}
 */
export function IfZero({children}) {
  const context = useContext(PluralContext)
  const count = context.count !== undefined ? context.count : 1

  return count === 0 ? children : null
}

/**
 * Show if not zero
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @returns {React.ReactNode}
 */
export function IfNotZero({children}) {
  const context = useContext(PluralContext)
  const count = context.count !== undefined ? context.count : 1

  return count !== 0 ? children : null
}

/**
 * Show if plural but not zero
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @returns {React.ReactNode}
 */
export function IfPluralNotZero({children}) {
  const context = useContext(PluralContext)
  const count = context.count !== undefined ? context.count : 1

  return count > 1 ? children : null
}
